import React from "react";
import type {BriefingOverrides, BriefingView} from "./briefingLogic";
import {assumptionValue, buildAssumptionDiff, computeView, INITIAL_OVERRIDES} from "./briefingLogic";

/**
 * In-session state of the briefing: dismissals, conversions, assumption
 * overrides (each carrying its required reason — the audit trail), fact
 * overrides, owner routing, the simulated fix round-trip and the Atlas panel.
 * Resets on reload, except the applied fix (kept in sessionStorage so a
 * confirmed correction never silently reverts within the session).
 */

const FIX_PENDING_KEY = 'daily-briefing.fix-pending';
const FIX_APPLIED_KEY = 'daily-briefing.fix-applied';
const STATE_KEY = 'daily-briefing.state.v1';

/**
 * The durable slice shared between the briefing and the move detail pages
 * (separate routes, separate providers): edits made on one surface must be
 * visible on the other, and confirmed corrections never silently revert.
 */
const PERSISTED_KEYS = ['dismissed', 'createdInitiatives', 'assumptionOverrides', 'factOverrides', 'askedOwners', 'blockedOwnerAsked', 'fixApplied'] as const;
type PersistedState = Pick<BriefingState, typeof PERSISTED_KEYS[number]>;

function loadPersisted(): Partial<PersistedState> {
    try {
        return JSON.parse(sessionStorage.getItem(STATE_KEY) ?? '{}') as Partial<PersistedState>;
    } catch {
        return {};
    }
}

function savePersisted(state: BriefingState): void {
    const slice = Object.fromEntries(PERSISTED_KEYS.map(k => [k, state[k]]));
    sessionStorage.setItem(STATE_KEY, JSON.stringify(slice));
}

export type BriefingState = BriefingOverrides & {
    /** Override-with-reason audit records on facts (keyed opportunity:dimension:record). */
    factOverrides: Record<string, {reason: string}>
    /** Diff banner for the latest assumption edit (blast radius). */
    assumptionBanner: string | null
    bannerVisible: boolean
    atlasOpen: boolean
    atlasQuestion: number | null
    highlightOpportunityId: string | null
    askedOwners: string[]
    blockedOwnerAsked: boolean
}

export type BriefingAction =
    | {type: 'dismiss', opportunityId: string, reason: string, note?: string}
    | {type: 'convert', opportunityId: string}
    | {type: 'setAssumption', id: string, value: number, reason: string}
    | {type: 'resetAssumption', id: string}
    | {type: 'clearAssumptionBanner'}
    | {type: 'overrideFact', itemKey: string, reason: string}
    | {type: 'applyFix'}
    | {type: 'dismissBanner'}
    | {type: 'openAtlas'}
    | {type: 'closeAtlas'}
    | {type: 'selectAtlasQuestion', index: number, highlightOpportunityId: string | null}
    | {type: 'askOwner', itemKey: string}
    | {type: 'askBlockedOwner'}

export function reducer(state: BriefingState, action: BriefingAction): BriefingState {
    switch (action.type) {
        case 'dismiss':
            if (!action.reason) return state; // reason is required — no silent dismissals
            return {...state, dismissed: {...state.dismissed, [action.opportunityId]: {reason: action.reason, note: action.note}}};
        case 'convert':
            return {...state, createdInitiatives: [...state.createdInitiatives, action.opportunityId]};
        case 'setAssumption': {
            if (!action.reason.trim()) return state; // an override cannot be saved without a reason
            const next: BriefingState = {
                ...state,
                assumptionOverrides: {
                    ...state.assumptionOverrides,
                    [action.id]: {value: action.value, reason: action.reason.trim()},
                },
            };
            const banner = buildAssumptionDiff(action.id, assumptionValue(state, action.id), action.value, computeView(state), computeView(next));
            return {...next, assumptionBanner: banner};
        }
        case 'resetAssumption': {
            const previous = state.assumptionOverrides[action.id];
            if (!previous) return state;
            const rest = {...state.assumptionOverrides};
            delete rest[action.id];
            const next: BriefingState = {...state, assumptionOverrides: rest};
            const banner = buildAssumptionDiff(action.id, previous.value, assumptionValue(next, action.id), computeView(state), computeView(next));
            return {...next, assumptionBanner: banner};
        }
        case 'clearAssumptionBanner':
            return {...state, assumptionBanner: null};
        case 'overrideFact':
            if (!action.reason.trim()) return state; // reason required — it is the audit trail
            return {...state, factOverrides: {...state.factOverrides, [action.itemKey]: {reason: action.reason.trim()}}};
        case 'applyFix':
            return {...state, fixApplied: true, bannerVisible: true, highlightOpportunityId: 'faciliCore-ifm-renewal'};
        case 'dismissBanner':
            return {...state, bannerVisible: false, highlightOpportunityId: null};
        case 'openAtlas':
            return {...state, atlasOpen: true};
        case 'closeAtlas':
            return {...state, atlasOpen: false, atlasQuestion: null, highlightOpportunityId: state.bannerVisible ? state.highlightOpportunityId : null};
        case 'selectAtlasQuestion':
            return {...state, atlasQuestion: action.index, highlightOpportunityId: action.highlightOpportunityId};
        case 'askOwner':
            return {...state, askedOwners: [...state.askedOwners, action.itemKey]};
        case 'askBlockedOwner':
            return {...state, blockedOwnerAsked: true};
        default:
            return state;
    }
}

function initialState(): BriefingState {
    return {
        ...INITIAL_OVERRIDES,
        factOverrides: {},
        assumptionBanner: null,
        bannerVisible: false,
        atlasOpen: false,
        atlasQuestion: null,
        highlightOpportunityId: null,
        askedOwners: [],
        blockedOwnerAsked: false,
        fixApplied: sessionStorage.getItem(FIX_APPLIED_KEY) === '1',
        ...loadPersisted(),
    };
}

export function markFixPending(): void {
    sessionStorage.setItem(FIX_PENDING_KEY, '1');
}

/** True exactly once: on the first briefing mount after a fix-route visit. */
function consumeFixPending(): boolean {
    if (sessionStorage.getItem(FIX_PENDING_KEY) !== '1') return false;
    sessionStorage.removeItem(FIX_PENDING_KEY);
    sessionStorage.setItem(FIX_APPLIED_KEY, '1');
    return true;
}

/** True when any fact of this dimension has been routed to its owner: parked, not low. */
export function isDimensionParked(state: BriefingState, opportunityId: string, cls: string): boolean {
    return state.askedOwners.some(k => k.startsWith(`${opportunityId}:${cls}:`));
}

type BriefingContextValue = {
    state: BriefingState
    view: BriefingView
    dispatch: React.Dispatch<BriefingAction>
}

const BriefingContext = React.createContext<BriefingContextValue | null>(null);

export const BriefingProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
    const [state, dispatch] = React.useReducer(reducer, undefined, initialState);

    React.useEffect(() => {
        if (consumeFixPending()) dispatch({type: 'applyFix'});
    }, []);

    React.useEffect(() => savePersisted(state), [state]);

    const view = React.useMemo(() => computeView(state), [state]);
    const value = React.useMemo(() => ({state, view, dispatch}), [state, view]);
    return <BriefingContext.Provider value={value}>{children}</BriefingContext.Provider>;
};

export function useBriefing(): BriefingContextValue {
    const ctx = React.useContext(BriefingContext);
    if (!ctx) throw new Error('useBriefing must be used inside BriefingProvider');
    return ctx;
}
